import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Autocomplete,
    Typography,
    CircularProgress,
} from "@mui/material";
import { Hotel } from "../../types/hotel.d";
import { CreateUserDTO, Role } from "../../types/user.d";
import UserService from "../../api/userService";
import { useAuth } from "../../context/authContext";

interface AddUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    hotels: Hotel[];
    selectedHotel: Hotel | null;
    refreshUsers: () => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, hotels, selectedHotel, refreshUsers }) => {
    const { user: currentUser } = useAuth();

    const [newUser, setNewUser] = useState<CreateUserDTO>({
        username: "",
        email: "",
        firstName: "",
        lastName: "",
        fk_Hotel_ID: selectedHotel ? selectedHotel.id : 0,
        role: "User",
        password: "",
    });

    const [selectedHotelForUser, setSelectedHotelForUser] = useState<Hotel | null>(selectedHotel);
    const [roles, setRoles] = useState<Role[]>([]);
    const [errors, setErrors] = useState<{ [key: string]: string | null }>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const data = await UserService.getRoles();
                setRoles(data);
            } catch (error) {
                console.error("Erreur lors du chargement des rôles:", error);
            }
        };
        fetchRoles();
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, checked, type } = e.target;
        setNewUser((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
    };

    const handleHotelChange = (event: any, value: Hotel | null) => {
        setSelectedHotelForUser(value);
        setNewUser((prev) => ({
            ...prev,
            fk_Hotel_ID: value ? value.id : 0,
        }));
        setErrors((prevErrors) => ({ ...prevErrors, hotelId: null }));
    };

    const validateFields = () => {
        const newErrors: { [key: string]: string | null } = {};

        if (!newUser.username) newErrors.username = "Veuillez entrer un nom d'utilisateur";

        if (!newUser.email || !/\S+@\S+\.\S+/.test(newUser.email) || newUser.email.length > 100) {
            newErrors.email = "Veuillez entrer un email valide (max 100 caractères).";
        }

        if (
            !newUser.password ||
            newUser.password.length < 8 ||
            !/(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>])/.test(newUser.password)
        ) {
            newErrors.password =
                "Le mot de passe doit contenir au moins 8 caractères, un chiffre et un caractère spécial.";
        }

        if (
            !newUser.firstName ||
            newUser.firstName.length > 50 ||
            !/^[A-Za-zÀ-ÿ]+(-[A-Za-zÀ-ÿ]+)*$/.test(newUser.firstName)
        ) {
            newErrors.firstName =
                "Le prénom est requis, doit contenir uniquement des lettres ou des tirets simples (max 50 caractères).";
        }

        if (
            !newUser.lastName ||
            newUser.lastName.length > 50 ||
            !/^[A-Za-zÀ-ÿ]+(-[A-Za-zÀ-ÿ]+)*$/.test(newUser.lastName)
        ) {
            newErrors.lastName =
                "Le nom de famille est requis, doit contenir uniquement des lettres ou des tirets simples (max 50 caractères).";
        }

        if (!newUser.role) {
            newErrors.role = "Le rôle est requis.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateFields()) return;

        const userData: CreateUserDTO = {
            ...newUser,
            fk_Hotel_ID: selectedHotelForUser ? selectedHotelForUser.id : 0,
        };

        setLoading(true);
        try {
            await UserService.createUser(userData);
            await refreshUsers();
            onClose();
        } catch (error: any) {
            console.error("Erreur lors de l'ajout de l'utilisateur:", error);
            let errorMsg = "L'ajout de l'utilisateur a échoué. Veuillez réessayer.";

            // À revoir éventuellement, mais pour l'instant c'est simple et ça fonctionne
            switch (error) {
                case 'Error creating user in Keycloak: {"errorMessage":"User exists with same email"}':
                    errorMsg = "Ce courriel est déjà utilisé";
                    break;
                case "Username already exists":
                    errorMsg = "Ce nom d'utilisateur existe déjà";
                    break;
            }
            setErrors({
                general: errorMsg,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DialogTitle>Ajouter un utilisateur</DialogTitle>
            <DialogContent>
                <TextField
                    label="Nom d'utilisateur"
                    name="username"
                    value={newUser.username}
                    onChange={handleInputChange}
                    fullWidth
                    margin="dense"
                    required
                    error={!!errors.username}
                    helperText={errors.username}
                />
                <TextField
                    label="Email"
                    name="email"
                    value={newUser.email}
                    onChange={handleInputChange}
                    fullWidth
                    margin="dense"
                    required
                    error={!!errors.email}
                    helperText={errors.email}
                />
                <TextField
                    label="Mot de passe"
                    name="password"
                    type="password"
                    value={newUser.password}
                    onChange={handleInputChange}
                    fullWidth
                    margin="dense"
                    required
                    error={!!errors.password}
                    helperText={errors.password}
                />
                <TextField
                    label="Prénom"
                    name="firstName"
                    value={newUser.firstName}
                    onChange={handleInputChange}
                    fullWidth
                    margin="dense"
                    required
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                />
                <TextField
                    label="Nom de famille"
                    name="lastName"
                    value={newUser.lastName}
                    onChange={handleInputChange}
                    fullWidth
                    margin="dense"
                    required
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                />

                <Autocomplete
                    options={hotels}
                    getOptionLabel={(option) => option.name}
                    value={selectedHotelForUser}
                    onChange={handleHotelChange}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Hôtel associé"
                            error={!!errors.hotelId}
                            helperText={errors.hotelId}
                            required
                        />
                    )}
                    fullWidth
                    sx={{ mt: 2 }}
                />

                <Autocomplete
                    options={roles.filter(
                        (role) => (currentUser?.role === "Admin" || role.name !== "Admin") && role.name !== "Manager" // Rôle inutile, à retirer du backend éventuellement
                    )}
                    getOptionLabel={(option) => option.name}
                    value={roles.find((role) => role.name === newUser.role) || null}
                    onChange={(event, value) => {
                        setNewUser((prev) => ({
                            ...prev,
                            role: value?.name || "",
                        }));
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Rôle"
                            required
                            error={!!errors.role}
                            helperText={errors.role}
                            margin="dense"
                        />
                    )}
                    fullWidth
                    sx={{ mt: 2 }}
                />

                {errors.general && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {errors.general}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Annuler
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {loading ? "Ajout en cours..." : "Ajouter"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddUserModal;
